import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import {
  $accountCardListStore,
  $accountCardReportStore,
  $cardListStore,
  $initialCurrenciesAvailableStore,
  $reportStore,
  $transactionStore,
} from '@lib/store';
import { useListState } from '@shared/hooks';
import { AccountUtils, cn, FormatUtils, LocalStorageUtils, PaletteUtils, PermissionUtils } from '@shared/utils';
import { ReportSecondaryTabs } from '../../models';
import {
  AccountType,
  ReportExportFileType,
  TransactionReportColumnsEnum,
  type AccountResponse,
  type CardResponse,
  type GenerateTransactionReportRequest,
} from '@shared/api';
import { AccountTable } from './accountTable';
import { TransactionTable } from '../creditTab';
import { ColumnsModal } from '../columnsModal';
import { ExportModal } from '../exportModal';
import { GeneratedFileModal } from '../generatedFileModal';
import { Button, Icon, SearchInput, Select, SelectDateRange, Spinner, Tabs } from '@shared/widgets';
import DownloadIcon from '@assets/icons/download.svg';
import PlusIcon from '@assets/icons/plus.svg';

const ColumnsEnum = {
  STATUS: 'Status',
  CARD: 'Card',
  DATE: 'Date',
  TRANSACTIONREFERENCE: 'TransactionReference',
  AMOUNT: 'Amount',
  FEE: 'Fee',
  TOTALSPENT: 'TotalSpent',
  CATEGORY: 'Category',
  RECEIPT: 'Receipt',
  NOTE: 'Note',
} as const;
export type ColumnsEnum = (typeof ColumnsEnum)[keyof typeof ColumnsEnum];

const COLUMNS = [
  ColumnsEnum.STATUS,
  ColumnsEnum.CARD,
  ColumnsEnum.DATE,
  ColumnsEnum.TRANSACTIONREFERENCE,
  ColumnsEnum.AMOUNT,
  ColumnsEnum.FEE,
  ColumnsEnum.TOTALSPENT,
  ColumnsEnum.CATEGORY,
  ColumnsEnum.RECEIPT,
  ColumnsEnum.NOTE,
];

const mapColumnToEnum = (column: string): TransactionReportColumnsEnum | undefined => {
  const columns: { [key: string]: TransactionReportColumnsEnum } = {
    [ColumnsEnum.STATUS]: TransactionReportColumnsEnum.STATUS,
    [ColumnsEnum.CARD]: TransactionReportColumnsEnum.CARD,
    [ColumnsEnum.DATE]: TransactionReportColumnsEnum.DATE,
    [ColumnsEnum.TRANSACTIONREFERENCE]: TransactionReportColumnsEnum.TRANSACTIONREFERENCE,
    [ColumnsEnum.AMOUNT]: TransactionReportColumnsEnum.AMOUNT,
    [ColumnsEnum.FEE]: TransactionReportColumnsEnum.FEE,
    [ColumnsEnum.TOTALSPENT]: TransactionReportColumnsEnum.TOTALSPENT,
    [ColumnsEnum.CATEGORY]: TransactionReportColumnsEnum.CATEGORY,
    [ColumnsEnum.RECEIPT]: TransactionReportColumnsEnum.RECEIPT,
    [ColumnsEnum.NOTE]: TransactionReportColumnsEnum.NOTE,
  };

  return columns[column];
};

export const ReportCreditTab = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const accountsStore = useUnit($accountCardListStore.store);
  const cardStore = useUnit($cardListStore.store);
  const transactionStore = useUnit($transactionStore.store);
  const accountReportStore = useUnit($accountCardReportStore.store);
  const reportStore = useUnit($reportStore.store);

  const [tab, setTab] = useState<ReportSecondaryTabs>(ReportSecondaryTabs.Transactions);
  const [search, setSearch] = useState('');
  const [transactionColumns, setTransactionColumns] = useState<string[]>(LocalStorageUtils.getCardTransactionColumns() || COLUMNS);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [account, setAccount] = useState<AccountResponse | undefined>(undefined);
  const [card, setCard] = useState<CardResponse | undefined>(undefined);
  const [dateRange, setDateRange] = useState<{ dateFrom?: Dayjs; dateTo?: Dayjs } | undefined>(undefined);
  const [showColumnsModal, setShowColumnsModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);

  const reportsCardsTransactionsState = useListState({ list: 'reportsCardsTransactions' });
  const reportsCardsAccountsState = useListState({ list: 'reportsCardsAccounts' });

  const { t } = useTranslation('reports');

  const accounts = useMemo(() => AccountUtils.getAccountsWithTransactionView(accountsStore.data), [accountsStore.data]);
  const isTransactionTab = tab === ReportSecondaryTabs.Transactions;

  useEffect(() => {
    $accountCardListStore.events.getAccounts({ AccountType: AccountType.Card });
    $cardListStore.events.getCards({ Count: 100 });

    return () => {
      $accountCardReportStore.events.clear();
      $accountCardListStore.events.clear();
      $transactionStore.events.clear();
      $cardListStore.events.clear();
    };
  }, []);

  const updateTransactions = () => {
    $transactionStore.events.getTransactions({
      Currency: currency,
      TextSearch: search,
      AccountIds: account?.id ? [account?.id] : undefined,
      CardId: card?.cardId,
      DateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      DateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      ReturnTotalCount: true,
      Count: reportsCardsTransactionsState.count,
      FromIndex: reportsCardsTransactionsState.fromIndex,
    });
  };
  useEffect(() => {
    if (tab != ReportSecondaryTabs.Transactions || !PermissionUtils.viewReportsTransactions()) return;
    updateTransactions();
  }, [tab, currency, search, dateRange, card, account, reportsCardsTransactionsState]);

  const updateAccounts = () => {
    $accountCardReportStore.events.getCardAccountBalanceReport({
      Currency: currency,
      ReturnTotalCount: true,
      Count: reportsCardsAccountsState.count,
      FromIndex: reportsCardsAccountsState.fromIndex,
    });
  };
  useEffect(() => {
    if (tab !== ReportSecondaryTabs.AccountsBalance || !PermissionUtils.viewReportsAccountBalance()) return;
    updateAccounts();
  }, [tab, currency, reportsCardsAccountsState]);

  const exportHandler = async (item: ReportExportFileType) => {
    setShowExportModal(false);
    if (isTransactionTab) {
      const columnsVisible = transactionColumns.map(mapColumnToEnum).filter((col) => !!col);
      const request: GenerateTransactionReportRequest = {
        Currency: currency,
        AccountId: account?.id,
        CardId: card?.cardId,
        ReportType: item,
        FromDate: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        ToDate: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        ColumnsVisible: columnsVisible,
      };
      const result = await $reportStore.events.generateTransactionReport(request);
      if (result.data) setShowGeneratedModal(true);
    } else {
      const result = await $reportStore.events.generateCardAccountBalanceReport({ ReportType: item, Currency: currency });
      if (result.data) setShowGeneratedModal(true);
    }
  };

  const changeTabHandler = (tab: ReportSecondaryTabs) => {
    setTab(tab);
    setCurrency(undefined);
  };

  const updateColumnsHandler = (activeColumns: string[]) => {
    setTransactionColumns(activeColumns);
    LocalStorageUtils.setCardTransactionColumns(activeColumns);
  };

  const tabOptions = useMemo(
    () =>
      [
        PermissionUtils.viewReportsTransactions() && { label: t('Transactions'), value: ReportSecondaryTabs.Transactions },
        PermissionUtils.viewReportsAccountBalance() && { label: t('Accounts Balance'), value: ReportSecondaryTabs.AccountsBalance },
      ].filter(Boolean) as { label: string; value: ReportSecondaryTabs }[],
    [t],
  );
  const accountOptions = useMemo(
    () => accounts.map((account) => ({ label: FormatUtils.toSliceNumber(account.accountNumber) || '', value: account })),
    [accounts],
  );
  const cardOptions = useMemo(
    () => cardStore.data?.data?.map((card) => ({ label: FormatUtils.toSliceNumber(card.cardNumber) || '', value: card })) || [],
    [cardStore.data?.data],
  );
  return (
    <div className='flex h-full w-full flex-col'>
      <div className='my-4 flex items-center gap-2'>
        {isTransactionTab && <SearchInput withDebounce value={search} onChange={setSearch} />}
        <div className='ml-auto flex gap-2'>
          {isTransactionTab && (
            <Button color='primary' onClick={() => setShowColumnsModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('buttons.addFilter')}
            </Button>
          )}
          <Button
            color='primary'
            disabled={isTransactionTab ? !transactionStore.data.data.length : !accountReportStore.data.data.length}
            onClick={() => setShowExportModal(true)}
          >
            <Icon icon={<DownloadIcon />} color='inherit' />
            {t('buttons.export')}
          </Button>
        </div>
      </div>
      <div className={cn('flex justify-between', 'max-lg:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName='h-full gap-0'
          labelClassName={cn('p-4', 'hover:bg-white dark:hover:bg-soft-black')}
          activeLabelClassName='bg-white dark:bg-soft-black'
          options={tabOptions}
          hideIndicator
          value={tab}
          onChange={changeTabHandler}
        />
        <div className={cn('flex gap-3 pb-6', 'max-md:flex-col max-md:gap-2')}>
          <Select
            className='min-w-[160px]'
            isLoading={currenciesAvailableStore.loading}
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
          {isTransactionTab && (
            <>
              <Select
                className='min-w-[146px]'
                isLoading={accountsStore.loading}
                nullValue={{ isEnable: true, text: t('placeholders.allAccounts', { ns: 'common' }) }}
                items={accountOptions}
                value={account}
                onChange={setAccount}
              />
              <Select
                className='min-w-[122px]'
                isLoading={cardStore.loading}
                nullValue={{ isEnable: true, text: t('placeholders.allCards', { ns: 'common' }) }}
                items={cardOptions}
                value={card}
                onChange={setCard}
              />
              <SelectDateRange value={dateRange} onChange={setDateRange} />
            </>
          )}
        </div>
      </div>
      <div className={cn('flex w-full', PaletteUtils.isVcfcPalette && 'flex-col rounded-b-xl bg-white p-6 dark:bg-soft-black')}>
        {PermissionUtils.viewReportsTransactions() && tab === ReportSecondaryTabs.Transactions && (
          <TransactionTable
            isLoading={transactionStore.loading}
            error={transactionStore.error}
            onRefresh={updateTransactions}
            columns={transactionColumns}
            items={transactionStore.data.data}
            totalItemCount={transactionStore.data.total}
            currentPage={reportsCardsTransactionsState.page}
            countItemPage={reportsCardsTransactionsState.count}
            onChangePage={reportsCardsTransactionsState.onChangePage}
            onChangeCountItemPage={reportsCardsTransactionsState.onChangeCount}
            onUpdate={updateTransactions}
          />
        )}
        {PermissionUtils.viewReportsAccountBalance() && tab === ReportSecondaryTabs.AccountsBalance && (
          <AccountTable
            isLoading={accountReportStore.loading}
            error={accountReportStore.error}
            onRefresh={updateAccounts}
            accounts={accountReportStore.data.data}
            totalItemCount={accountReportStore.data.total}
            currentPage={reportsCardsAccountsState.page}
            countItemPage={reportsCardsAccountsState.count}
            onChangePage={reportsCardsAccountsState.onChangePage}
            onChangeCountItemPage={reportsCardsAccountsState.onChangeCount}
          />
        )}
      </div>
      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} onExport={exportHandler} />
      {reportStore.loading && <Spinner position />}
      <ColumnsModal
        show={showColumnsModal}
        onClose={() => setShowColumnsModal(false)}
        columns={COLUMNS}
        activeColumns={transactionColumns}
        onSave={updateColumnsHandler}
      />
      <GeneratedFileModal open={showGeneratedModal} onClose={() => setShowGeneratedModal(false)} />
    </div>
  );
};
